import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {Router} from 'react-router-dom';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';

import {nutApolloClient} from '../../../../apollo-client';
import {appHistory} from '../../../../history';
import {store} from '../../../../store';

import {UserSetupSidebar} from './user-setup-sidebar';
import type {Props as UserSetupSidebarProps} from './user-setup-sidebar';

const SETUP_USER_SIDEBAR_REACT_MOUNT_ID = 'setup-user-sidebar-react-mount';
const USERS_SIDEBAR_SECTION_REACT_PROPS_ID = 'users-sidebar-section-react-props';
const TEAMS_SIDEBAR_SECTION_REACT_PROPS_ID = 'teams-sidebar-section-react-props';

export function mountUserSetupSidebar() {
    const setupUserSidebarMount = document.getElementById(SETUP_USER_SIDEBAR_REACT_MOUNT_ID);

    if (setupUserSidebarMount) {
        // Initializing with default placeholder values for the sake of flow
        const userSetupSidebarProps: UserSetupSidebarProps = {
            usersSection: {
                enabledUsers: {
                    count: 0,
                    urlPath: '',
                },
                unmanagedUsers: {
                    count: 0,
                    urlPath: '',
                },
                disabledUsers: {
                    count: 0,
                    urlPath: '',
                },
                unlicensedUsers: {
                    count: 0,
                    urlPath: '',
                },
                pendingUsers: {
                    count: 0,
                    urlPath: '',
                },
            },
            teamsSection: {
                teams: [],
            },
            status: setupUserSidebarMount ? setupUserSidebarMount.dataset.status : undefined,
        };

        /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
         * Users sidebar section props - come from user/sidebar.phtml
         * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
        const usersSectionPropsElement = document.getElementById(
            USERS_SIDEBAR_SECTION_REACT_PROPS_ID
        );

        if (
            usersSectionPropsElement instanceof HTMLInputElement &&
            usersSectionPropsElement.value
        ) {
            const usersSectionProps = JSON.parse(usersSectionPropsElement.value);
            userSetupSidebarProps.usersSection = {
                enabledUsers: {
                    count: Number.parseInt(usersSectionProps.enabledUsersCount),
                    urlPath: usersSectionProps.enabledUsersUrlPath,
                },
                unmanagedUsers: {
                    count: Number.parseInt(usersSectionProps.unmanagedUsersCount),
                    urlPath: usersSectionProps.unmanagedUsersUrlPath,
                },
                disabledUsers: {
                    count: Number.parseInt(usersSectionProps.disabledUsersCount),
                    urlPath: usersSectionProps.disabledUsersUrlPath,
                },
                unlicensedUsers: {
                    count: Number.parseInt(usersSectionProps.unlicensedUsersCount),
                    urlPath: usersSectionProps.unlicensedUsersUrlPath,
                },
                pendingUsers: {
                    count: Number.parseInt(usersSectionProps.pendingUsersCount),
                    urlPath: usersSectionProps.pendingUsersUrlPath,
                },
            };
        }

        /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
         * Teams sidebar section props - come from user/sidebar.phtml
         * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
        const teamsSectionPropsElement = document.getElementById(
            TEAMS_SIDEBAR_SECTION_REACT_PROPS_ID
        );

        if (
            teamsSectionPropsElement instanceof HTMLInputElement &&
            teamsSectionPropsElement.value
        ) {
            const teamsSectionProps = JSON.parse(teamsSectionPropsElement.value);
            // @ts-expect-error
            userSetupSidebarProps.teamsSection.teams = teamsSectionProps.teams.map((team) => ({
                name: team.name,
                urlPath: team.urlPath,
                usersCount: Number.parseInt(team.usersCount),
            }));
        }

        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <UserSetupSidebar {...userSetupSidebarProps} />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            setupUserSidebarMount
        );
    }
}
